import { useEffect, type ReactNode } from "react";
import Button from "../Button/Button";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  panelClassName?: string;
  bodyClassName?: string;
};

const Modal = ({ isOpen, onClose, title, children, panelClassName, bodyClassName }: ModalProps) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      onClick={onClose}
    >
      <div
        className={`my-auto flex max-h-[calc(100vh-3rem)] w-full max-w-2xl flex-col rounded-xl border border-myteengig-border bg-white p-6 shadow-lg ${panelClassName ?? ""}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 id="modal-title" className="text-xl font-semibold text-myteengig-text-primary">
            {title}
          </h2>
          <Button
            variant="tertiary"
            size="sm"
            className="px-2 py-1 text-myteengig-text-muted hover:bg-myteengig-background hover:text-myteengig-text-primary"
            onClick={onClose}
            aria-label="Close modal"
          >
            Close
          </Button>
        </div>
        <div className={`min-h-0 overflow-y-auto pr-1 ${bodyClassName ?? ""}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
